import 'styles/rentman.css';
import RentmanCalendar from 'components/RentmanCalendar';
import QuoteLine from 'components/QuoteLine';
import RentmanNewQuote from 'components/RentmanNewQuote';
import QuoteEdit from 'components/QuoteEdit';
import QuoteNew from 'components/QuoteNew';
import QuoteDelivery from 'components/QuoteDelivery';
import QuoteStaff from 'components/QuoteStaff';
import QuoteDeliveryBill from 'components/QuoteDeliveryBill';
import RentmanMobile from 'components/RentmanMobile';
import useIsMobile from 'hooks/useIsMobile';
import { useState, useEffect, useRef } from 'react';
import api from "api";
import StatsPopup from './StatsPopup';
import { useAuth } from 'auth/AuthContext';


const SwitchCancelled = (
  {
    printCancelled,
    setPrintCancelled
  }
) => {
  // 
  const handleChange = () => {
        console.log("handleChange déclenché !");
    setPrintCancelled(prev => !prev);
  };

  return (
    <label className="RentmanToolbarLabel">
      Afficher les devis annulés
      <input
        type="checkbox"
        checked={printCancelled}
        onChange={handleChange}
      />
    </label>
  );

};


const ClientFilter = (
  {
    customers,
    clientFilter,
    setClientFilter
  }
) => {

  const handleChange = (event) => {
    setClientFilter(event.target.value);
  };

  const handleClear = () => {
    setClientFilter("");
  };

  return (
    <div className="RentmanClientFilter">
      <label className="RentmanToolbarLabel">
        Filtrer par client / évènement
        <input
          list="rentman-customers"
          type="text"
          value={clientFilter}
          onChange={handleChange}
          placeholder="Client ou évènement"
        />
      </label>
      {
        clientFilter
        &&
        <button type="button" onClick={handleClear} title="Annuler le filtre">✖</button>
      }
      <datalist id="rentman-customers">
        {customers.map((item) => (
          <option key={`${item.name} - ${item.phone}`} value={item.name}/>
        ))}
      </datalist>
    </div>
  );

};


function Rentman() {

  const [startDate, setStartDate] = useState(null); // champ date à partir de laquelle afficher la liste des devis
  const [editQuote, setEditQuote] = useState(false); // flag pour afficher l'édition du devis
  const [newQuote, setNewQuote] = useState(false); // flag pour afficher la création d'un devis
  const [selectedQuote, setSelectedQuote] = useState(null); // devis sélectionné
  const [quotes, setQuotes] = useState([]); // liste des devis
  const [reloadQuotes, setReloadQuotes] =  useState(false) // flag pour la mise à jour de la liste des devis
  const [ delivery, setDelivery ] = useState(false); // flag pour les frais de transport
  const [ staff, setStaff ] = useState(false); // flag pour le staff
  const [ deliveryBill, setDeliveryBill ] = useState(false); // flag bon de livraison
  const [printCancelled, setPrintCancelled] = useState(true); // flag d'affichage des devis annulés
  const [clientFilter, setClientFilter] = useState(""); // filtre par nom de client (annule les autres filtres)
  const [customers, setCustomers] = useState([]); // liste des clients pour l'autocomplétion
  const isMobile = useIsMobile(); // affichage dédié pour les petits écrans
  const savedQuoteId = useRef(null); // id du devis ouvert en édition, transport ou équipe
  // Pour le popup stats
  const [openStats, setOpenStats] = useState(false);
  const { logout } = useAuth();

  // wrapper pour sauvegarder l'id du devis avant d'ouvrir l'édition
  const openEditQuote = (value, quoteId = null) => {
    if (value) savedQuoteId.current = quoteId;
    setEditQuote(value);
  };
  // wrapper pour sauvegarder l'id du devis avant d'ouvrir la fenêtre transport
  const openDelivery = (value, quoteId = null) => {
    if (value) savedQuoteId.current = quoteId;
    setDelivery(value);
  };
  // wrapper pour sauvegarder l'id du devis avant d'ouvrir la fenêtre équipe
  const openStaff = (value, quoteId = null) => {
    if (value) savedQuoteId.current = quoteId;
    setStaff(value);
  };

  // récupérer la date d'affichage depuis l'environnement du client + flag d'affichage des devis annulés
  useEffect(() => {
    const saved = localStorage.getItem("startDate");
    if (saved) {
      setStartDate(parseInt(saved));
      setReloadQuotes(true);
    }
    else {
      setStartDate(Date.now());
      setReloadQuotes(true);
    }
    const value = localStorage.getItem("printCancelled");
    if (value) {
      setPrintCancelled(value==='true');
    }
    else {
      localStorage.setItem('printCancelled',printCancelled);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // récupère la liste des clients pour l'autocomplétion du filtre client
  useEffect(() => {
    api.get('customers')
    .then(response => setCustomers(response.data));
  }, []);

  // gère le changement de date d'affichage
  const changeDate = (date) => {
    const newDate = date.getTime();
    localStorage.setItem("startDate", newDate);
    setStartDate(newDate);
    setReloadQuotes(true);
  };

  // (re)charger la liste des devis selon la date/annulés, sauf si un filtre client est actif
  useEffect(() => {
    if (clientFilter.trim() !== "") return;
    if (reloadQuotes) {
      setReloadQuotes(false);
      api.get(`quotes/${startDate}`)
      .then(response => {
        const quotes = response.data
        setQuotes(quotes);
      });
    }
  }, [startDate, reloadQuotes, clientFilter]);

  // filtre client : annule les filtres date/annulés et affiche tous les devis du client (avec un léger debounce)
  useEffect(() => {
    const name = clientFilter.trim();
    if (!name) {
      setReloadQuotes(true); // revient aux 2 niveaux de filtres initiaux
      return;
    }
    const timer = setTimeout(() => {
      api.get(`quotes/byclient/${encodeURIComponent(name)}`)
      .then(response => setQuotes(response.data));
    }, 250);
    return () => clearTimeout(timer);
  }, [clientFilter]);

  // désélectionne le devis à la sortie des éditions et restore le scroll
  useEffect(()=>{
    if (!editQuote) {
      setSelectedQuote(null);
      const id = savedQuoteId.current;
      if (!id) return;
      let attempt = 0;
      const tryScroll = () => {
        const el = document.getElementById(`quote-${id}`);
        if (el && (el.firstElementChild || attempt >= 10)) {
          el.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        } else if (attempt < 30) {
          attempt++;
          setTimeout(tryScroll, 100);
        }
      };
      setTimeout(tryScroll, 50);
    }
  },[editQuote]);

  // restore scroll à la fermeture de la fenêtre transport
  useEffect(()=>{
    if (!delivery) {
      setSelectedQuote(null);
      const id = savedQuoteId.current;
      if (!id) return;
      let attempt = 0;
      const tryScroll = () => {
        const el = document.getElementById(`quote-${id}`);
        if (el && (el.firstElementChild || attempt >= 10)) {
          el.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        } else if (attempt < 30) {
          attempt++;
          setTimeout(tryScroll, 100);
        }
      };
      setTimeout(tryScroll, 50);
    }
  },[delivery]);

  // restore scroll à la fermeture de la fenêtre équipe
  useEffect(()=>{
    if (!staff) {
      setSelectedQuote(null);
      const id = savedQuoteId.current;
      if (!id) return;
      let attempt = 0;
      const tryScroll = () => {
        const el = document.getElementById(`quote-${id}`);
        if (el && (el.firstElementChild || attempt >= 10)) {
          el.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        } else if (attempt < 30) {
          attempt++;
          setTimeout(tryScroll, 100);
        }
      };
      setTimeout(tryScroll, 50);
    }
  },[staff]);

  useEffect(() => {
    localStorage.setItem('printCancelled',printCancelled);
  }, [printCancelled]);

  if (isMobile) return <RentmanMobile />;

  return (
    <>
      {
        editQuote
        &&
        !newQuote
        &&
        !staff
        &&
        !delivery
        &&
        !deliveryBill
        &&
        <QuoteEdit // édition du devis
          selectedQuote={selectedQuote} // devis à éditer
          setEditQuote={setEditQuote} // flag pour fermer le mode édition
          setReloadQuotes={setReloadQuotes} // flag pour mettre à jour la liste des devis
        />
      }
      {
        newQuote
        &&
        !editQuote
        &&
        !staff
        &&
        !delivery
        &&
        !deliveryBill
        &&
        <QuoteNew // création du devis
          setNewQuote={setNewQuote} // flag pour fermer le mode création
          setReloadQuotes={setReloadQuotes} // flag pour recharger la liste des devis
          setEditQuote={openEditQuote} // flag pour ouvrir le mode modification
          setSelectedQuote={setSelectedQuote} // devis à modifier
        />
      }
      {
        !newQuote
        &&
        !editQuote
        &&
        !staff
        &&
        !delivery
        &&
        !deliveryBill
        &&
          <div className="Rentman" style={{position:'relative'}}>
            <div className="RentmanToolbar">
              <div className="RentmanToolbarSide RentmanToolbarLeft">
                <div className={`RentmanToolbarItem${clientFilter.trim() !== "" ? " RentmanToolbarItemDisabled" : ""}`}>
                  <SwitchCancelled
                    printCancelled = {printCancelled}
                    setPrintCancelled = {setPrintCancelled}
                  />
                </div>
                <div className="RentmanToolbarItem">
                  <ClientFilter
                    customers={customers}
                    clientFilter={clientFilter}
                    setClientFilter={setClientFilter}
                  />
                </div>
              </div>
              <div className={`RentmanToolbarItem RentmanToolbarCenter${clientFilter.trim() !== "" ? " RentmanToolbarItemDisabled" : ""}`}>
                <RentmanCalendar // choix de la date à afficher
                  startDate={new Date(startDate)}
                  onChange={changeDate}
                />
              </div>
              <div className="RentmanToolbarSide RentmanToolbarRight">
                <div className="RentmanToolbarItem">
                  <button className="RentmanToolbarButton" onClick={()=>setOpenStats(true)} title="Statistiques devis">
                    📊 Stats
                  </button>
                </div>
                <div className="RentmanToolbarItem">
                  <button className="RentmanToolbarButton" onClick={logout} title="Se déconnecter">
                    🔒 Déconnexion
                  </button>
                </div>
              </div>
            </div>
              {/* Popup stats */}
              <StatsPopup open={openStats} onClose={()=>setOpenStats(false)} />
            <RentmanNewQuote // bouton de la création de devis
              setNewQuote={setNewQuote}
            />
          { 
            quotes
            &&
            quotes.map(
              (q) => 
                <QuoteLine // affichage d'un devis en ligne
                  key={q.id} // key du map
                  quoteid={q.id} // id du devis
                  setSelectedQuote={setSelectedQuote} // pour sélectionner le devis
                  setEditQuote={openEditQuote} // pour ouvrir le mode édition
                  setDelivery={openDelivery} // pour ouvrir le mode frais de transport
                  setStaff={openStaff} // pour ouvrir le mode staff
                  setDeliveryBill={setDeliveryBill} // pour ouvrir le mode bon de livraison
                  setReloadQuotes={setReloadQuotes} // pour recharger la liste des devis
                  printCancelled={clientFilter.trim() !== "" ? true : printCancelled}
                />
            )
          }
        </div>
      }
      {
        !newQuote
        &&
        !editQuote
        &&
        !staff
        &&
        delivery
        &&
        !deliveryBill
        &&
        <QuoteDelivery
          setDelivery={setDelivery}
          selectedQuote={selectedQuote}
        />
      }
      {
        !newQuote
        &&
        !editQuote
        &&
        staff
        &&
        !delivery
        &&
        !deliveryBill
        &&
        <QuoteStaff
          setStaff={setStaff}
          selectedQuote={selectedQuote}
        />
      }
      {
        deliveryBill
        &&
        <QuoteDeliveryBill
          setDeliveryBill={setDeliveryBill}
          selectedQuote={selectedQuote}
        />
      }
    </>
  );
}

export default Rentman;
